import React, { useState } from 'react';
import {
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaClock,
} from 'react-icons/fa';
import type { IconType } from 'react-icons';
import emailjs from '@emailjs/browser';

const Icon = (Cmp: IconType, className?: string) =>
  React.createElement(Cmp as React.ComponentType<any>, { className });

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    inquiryType: '',
    message: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const serviceId = 'service_u5x1t7g';
    const templateId = 'template_0jp3m0j';
    const publicKey = 'MmHPzHHwRZt5nou_c';

    const templateParams = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      title: formData.inquiryType,
      message: formData.message
    };

    emailjs.send(serviceId, templateId, templateParams, publicKey)
      .then((response) => {
        alert('Your message has been sent! 🎉');
        console.log('SUCCESS!', response.status, response.text);
        setFormData({
          name: '',
          email: '',
          phone: '',
          inquiryType: '',
          message: ''
        });
      })
      .catch((err) => {
        alert('Oops! Something went wrong. Please try again.');
        console.error('EmailJS error:', err);
      });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="pt-16 sm:pt-18 lg:pt-16 min-h-screen bg-cream">
      {/* Hero Header */}
      <div className="bg-gradient-to-b from-orange-50 to-cream py-10 sm:py-12 lg:py-16">
        <div className="container-base text-center">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-pacifico text-orange-500 mb-2 sm:mb-3">
            Contact Us
          </h1>
          <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-xl mx-auto px-4">
            We'd love to hear from you
          </p>
        </div>
      </div>

      {/* Main Content */}
      <section className="section">
        <div className="container-base">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="card-elevated p-6 sm:p-8">
                <h2 className="text-h3 text-orange-500 font-pacifico mb-6">
                  Get in Touch
                </h2>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="input-label">
                        Name
                      </label>
                      <input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="input"
                        placeholder="Your name"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="input-label">
                        Email
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="input"
                        placeholder="your@email.com"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="phone" className="input-label">
                        Phone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="input"
                        placeholder="(123) 456-7890"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="inquiryType" className="input-label">
                        Inquiry Type
                      </label>
                      <select
                        id="inquiryType"
                        name="inquiryType"
                        value={formData.inquiryType}
                        onChange={handleChange}
                        className="input"
                        required
                      >
                        <option value="">Select an option</option>
                        <option value="catering">Catering</option>
                        <option value="general">General Questions</option>
                        <option value="franchise">Franchise</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="input-label">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      className="input resize-none"
                      placeholder="How can we help you?"
                      required
                    />
                  </div>

                  <button type="submit" className="btn-primary w-full btn-lg">
                    Send Message
                  </button>
                </form>
              </div>
            </div>

            {/* Contact Info Sidebar */}
            <div className="lg:col-span-2 space-y-6">
              {/* Contact Details Card */}
              <div className="card p-6">
                <h3 className="text-h4 text-gray-900 mb-5">Contact Info</h3>

                <ul className="space-y-4">
                  <li>
                    <a href="tel:8437076860" className="flex items-center gap-4 text-gray-700 hover:text-orange-500 transition-colors group">
                      <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center text-orange-500 group-hover:bg-orange-500 group-hover:text-white transition-colors">
                        {Icon(FaPhoneAlt, 'w-4 h-4')}
                      </div>
                      <span className="text-body">843‑707‑6860</span>
                    </a>
                  </li>

                  <li>
                    <a href="mailto:info@alljoydonut.com" className="flex items-center gap-4 text-gray-700 hover:text-orange-500 transition-colors group">
                      <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center text-orange-500 group-hover:bg-orange-500 group-hover:text-white transition-colors">
                        {Icon(FaEnvelope, 'w-4 h-4')}
                      </div>
                      <span className="text-body">info@alljoydonut.com</span>
                    </a>
                  </li>

                  <li>
                    <a
                      href="https://www.google.com/maps?q=22+Heyward+St,+Bluffton,+SC"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-4 text-gray-700 hover:text-orange-500 transition-colors group"
                    >
                      <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center text-orange-500 group-hover:bg-orange-500 group-hover:text-white transition-colors">
                        {Icon(FaMapMarkerAlt, 'w-4 h-4')}
                      </div>
                      <span className="text-body">22 Heyward St, Bluffton SC</span>
                    </a>
                  </li>

                  <li className="flex items-center gap-4 text-gray-700">
                    <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center text-orange-500">
                      {Icon(FaClock, 'w-4 h-4')}
                    </div>
                    <span className="text-body">Fri – Sun, 7 am – 1 pm</span>
                  </li>
                </ul>
              </div>

              {/* Franchise CTA Card */}
              <div className="card bg-gradient-to-br from-orange-500 to-orange-600 p-6 text-white">
                <h3 className="text-h4 text-white mb-2">Interested in Franchising?</h3>
                <p className="text-white/90 text-body-sm mb-4">
                  Join the Alljoy family and bring joy to your community.
                </p>
                <button
                  onClick={() => {
                    setFormData(prev => ({ ...prev, inquiryType: 'franchise' }));
                    document.getElementById('inquiryType')?.focus();
                  }}
                  className="btn bg-white text-orange-500 hover:bg-orange-50 w-full"
                >
                  Contact Us Now!
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
